import openaiService from './openaiService';
import { getApiKey } from './authService';

export interface IngredientSubstitutionData {
  original: string;
  substitute: string;
  ratio: string;
  notes: string;
  confidence: 'high' | 'medium' | 'low';
}

export interface SubstitutionRequest {
  ingredient: string;
  reason?: string; // e.g., "allergic to nuts", "don't have this ingredient", "want healthier option"
  cuisine?: string; // context from the recipe
  recipeType?: string; // e.g., "baking", "sauce", "main dish"
}

export interface SubstitutionResponse {
  substitutions: IngredientSubstitutionData[];
  generalAdvice?: string;
}

export interface RecipeRewriteRequest {
  originalIngredients: string;
  substitution: {
    original: string;
    substitute: string;
    ratio: string;
  };
  recipeTitle: string;
  recipeSteps?: string[];
}

export interface RecipeRewriteResponse {
  updatedIngredients: string;
  updatedSteps?: string[];
  changes: string[];
}

class IngredientSubstitutionService {
  /**
   * Get ingredient substitutions using AI
   * @param request Substitution request details
   * @returns Promise resolving to substitution suggestions
   */
  async getSubstitutions(request: SubstitutionRequest): Promise<SubstitutionResponse> {
    try {
      // Ensure we have an API key
      const apiKey = await getApiKey();
      openaiService.setApiKey(apiKey);

      const prompt = this.buildSubstitutionPrompt(request);
      const response = await openaiService.getChatCompletion(prompt);
      return this.parseSubstitutionResponse(response);
    } catch (error) {
      console.error('Error getting ingredient substitutions:', error);
      throw new Error(`Failed to get substitutions: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get substitutions for multiple ingredients at once
   * @param ingredients Array of ingredient strings
   * @param context Recipe context information
   * @returns Promise resolving to substitution map
   */
  async getBulkSubstitutions(
    ingredients: string[], 
    context: { recipeTitle?: string; cuisine?: string }
  ): Promise<Map<string, IngredientSubstitutionData[]>> {
    try {
      const apiKey = await getApiKey();
      openaiService.setApiKey(apiKey);

      const prompt = this.buildBulkSubstitutionPrompt(ingredients, context);
      const response = await openaiService.getChatCompletion(prompt);
      return this.parseBulkSubstitutionResponse(response, ingredients);
    } catch (error) {
      console.error('Error getting bulk ingredient substitutions:', error);
      throw new Error(`Failed to get bulk substitutions: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Build the AI prompt for ingredient substitution
   * @param request Substitution request
   * @returns Formatted prompt string
   */
  private buildSubstitutionPrompt(request: SubstitutionRequest): string {
    const { ingredient, reason, cuisine, recipeType } = request;
    
    let context = '';
    if (cuisine) {
      context += `This is for a ${cuisine} recipe. `;
    }
    if (recipeType) {
      context += `The recipe type is ${recipeType}. `;
    }
    if (reason) {
      context += `The reason for substitution: ${reason}. `;
    }

    return `You are a professional chef and cooking expert. I need ingredient substitution advice.

${context}

I need to substitute: "${ingredient}"

Please provide 3-5 practical substitution options in this EXACT JSON format:

{
  "substitutions": [
    {
      "original": "${ingredient}",
      "substitute": "substitute ingredient name",
      "ratio": "1:1" or "2 tbsp for 1 tbsp" or specific ratio,
      "notes": "brief explanation of flavor/texture impact and usage tips",
      "confidence": "high" | "medium" | "low"
    }
  ],
  "generalAdvice": "optional general cooking tips for this substitution context"
}

Focus on:
- Common, accessible alternatives
- Maintaining flavor profile when possible  
- Considering texture and cooking properties
- Noting any significant taste or texture changes
- Confidence level based on how well the substitute works

Respond only with valid JSON, no additional text.`;
  }

  /**
   * Build the AI prompt for bulk ingredient substitutions
   * @param ingredients List of ingredients
   * @param context Recipe context
   * @returns Formatted prompt string
   */
  private buildBulkSubstitutionPrompt(
    ingredients: string[], 
    context: { recipeTitle?: string; cuisine?: string }
  ): string {
    const contextStr = context.recipeTitle 
      ? `for the recipe "${context.recipeTitle}"` 
      : 'for this recipe';
    
    const cuisineStr = context.cuisine 
      ? ` (${context.cuisine} cuisine)` 
      : '';

    return `You are a professional chef. I need ingredient substitution options ${contextStr}${cuisineStr}.

For the ingredients listed below, provide common substitution options for any that are possible seperatly:

Ingredients:
${ingredients.map((ing, i) => `${i + 1}. ${ing}`).join('\n')}

Please respond in this EXACT JSON format:

{
  "bulkSubstitutions": {
    "${ingredients[0]}": [
      {
        "original": "${ingredients[0]}",
        "substitute": "substitute name",
        "ratio": "1:1 or specific ratio",
        "notes": "brief notes on usage",
        "confidence": "high" | "medium" | "low"
      }
    ]
    // ... continue for ingredients
  }
}

Focus on practical, accessible substitutes that maintain the dish's integrity. Only include the JSON response, no additional text.`;
  }

  /**
   * Parse the AI response for single ingredient substitution
   * @param response Raw AI response
   * @returns Parsed substitution data
   */
  private parseSubstitutionResponse(response: string): SubstitutionResponse {
    try {
      // Clean the response of any non-JSON content
      const jsonStart = response.indexOf('{');
      const jsonEnd = response.lastIndexOf('}') + 1;
      
      if (jsonStart === -1 || jsonEnd <= jsonStart) {
        throw new Error('No valid JSON found in response');
      }
      
      const jsonStr = response.slice(jsonStart, jsonEnd);
      const parsed = JSON.parse(jsonStr);
      
      // Validate the structure
      if (!parsed.substitutions || !Array.isArray(parsed.substitutions)) {
        throw new Error('Invalid substitution response format');
      }

      return {
        substitutions: parsed.substitutions.map((sub: IngredientSubstitutionData) => ({
          original: sub.original || '',
          substitute: sub.substitute || '',
          ratio: sub.ratio || '1:1',
          notes: sub.notes || '',
          confidence: ['high', 'medium', 'low'].includes(sub.confidence) ? sub.confidence : 'medium'
        })),
        generalAdvice: parsed.generalAdvice
      };
    } catch (error) {
      console.error('Error parsing substitution response:', error);
      console.error('Raw response:', response);
      
      // Return a fallback response
      return {
        substitutions: [{
          original: 'ingredient',
          substitute: 'No substitutions available',
          ratio: '1:1',
          notes: 'Unable to process substitution request at this time.',
          confidence: 'low'
        }],
        generalAdvice: 'Try searching online for ingredient substitution charts.'
      };
    }
  }

  /**
   * Parse the AI response for bulk ingredient substitutions
   * @param response Raw AI response
   * @param originalIngredients Original ingredient list
   * @returns Map of ingredient to substitutions
   */
  private parseBulkSubstitutionResponse(
    response: string, 
    originalIngredients: string[]
  ): Map<string, IngredientSubstitutionData[]> {
    const result = new Map<string, IngredientSubstitutionData[]>();
    
    try {
      const jsonStart = response.indexOf('{');
      const jsonEnd = response.lastIndexOf('}') + 1;
      
      if (jsonStart === -1 || jsonEnd <= jsonStart) {
        throw new Error('No valid JSON found in bulk response');
      }
      
      const jsonStr = response.slice(jsonStart, jsonEnd);
      const parsed = JSON.parse(jsonStr);
      
      if (!parsed.bulkSubstitutions || typeof parsed.bulkSubstitutions !== 'object') {
        throw new Error('Invalid bulk substitution response format');
      }

      // Process each ingredient substitution
      for (const ingredient of originalIngredients) {
        const substitutions = parsed.bulkSubstitutions[ingredient];
        if (substitutions && Array.isArray(substitutions)) {
          result.set(ingredient, substitutions.map((sub: IngredientSubstitutionData) => ({
            original: sub.original || ingredient,
            substitute: sub.substitute || '',
            ratio: sub.ratio || '1:1',
            notes: sub.notes || '',
            confidence: ['high', 'medium', 'low'].includes(sub.confidence as string) ? sub.confidence as 'high' | 'medium' | 'low' : 'medium'
          })));
        } else {
          // Provide fallback for missing ingredients
          result.set(ingredient, [{
            original: ingredient,
            substitute: 'No substitutions found',
            ratio: '1:1',
            notes: 'Substitution information not available',
            confidence: 'low'
          }]);
        }
      }
      
    } catch (error) {
      console.error('Error parsing bulk substitution response:', error);
      console.error('Raw response:', response);
      
      // Return fallback for all ingredients
      originalIngredients.forEach(ingredient => {
        result.set(ingredient, [{
          original: ingredient,
          substitute: 'Unable to process',
          ratio: '1:1',
          notes: 'Substitution service temporarily unavailable',
          confidence: 'low'
        }]);
      });
    }
    
    return result;
  }

  /**
   * Rewrite recipe with ingredient substitution
   * @param request Recipe rewrite request
   * @returns Promise resolving to updated recipe data
   */
  async rewriteRecipeWithSubstitution(request: RecipeRewriteRequest): Promise<RecipeRewriteResponse> {
    try {
      const apiKey = await getApiKey();
      openaiService.setApiKey(apiKey);

      const prompt = this.buildRecipeRewritePrompt(request);

      const response = await openaiService.getChatCompletion(prompt);
      return this.parseRecipeRewriteResponse(response, request);
    } catch (error) {
      console.error('Error rewriting recipe:', error);
      throw new Error(`Failed to rewrite recipe: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Build the AI prompt for recipe rewriting with substitution
   * @param request Recipe rewrite request
   * @returns Formatted prompt string
   */
  private buildRecipeRewritePrompt(request: RecipeRewriteRequest): string {
    const { originalIngredients, substitution, recipeTitle, recipeSteps } = request;

    return `You are a professional chef helping to rewrite a recipe with an ingredient substitution.

Recipe: "${recipeTitle}"
Substitution: Replace "${substitution.original}" with "${substitution.substitute}" (ratio: ${substitution.ratio})

Original Ingredients:
${originalIngredients}

${recipeSteps ? `Original Steps:
${recipeSteps.map((step, i) => `${i + 1}. ${step}`).join('\n')}` : ''}

Please provide the updated recipe in this EXACT JSON format:

{
  "updatedIngredients": "updated ingredients list with the substitution applied, maintaining the same format as original",
  ${recipeSteps ? '"updatedSteps": ["updated step 1", "updated step 2", ...], // only if cooking method changes are needed' : ''}
  "changes": ["brief description of each change made to accommodate the substitution"]
}

Guidelines:
- Apply the substitution ratio correctly to ingredient amounts
- Adjust cooking times/methods only if the substitution requires it
- Maintain the same ingredient list format and style
- Note any important changes in the "changes" array
- Keep the same recipe structure and flow

Respond only with valid JSON, no additional text.`;
  }

  /**
   * Parse the AI response for recipe rewriting
   * @param response Raw AI response
   * @param originalRequest Original request for fallback
   * @returns Parsed recipe rewrite data
   */
  private parseRecipeRewriteResponse(response: string, originalRequest: RecipeRewriteRequest): RecipeRewriteResponse {
    try {
      const jsonStart = response.indexOf('{');
      const jsonEnd = response.lastIndexOf('}') + 1;
      
      if (jsonStart === -1 || jsonEnd <= jsonStart) {
        throw new Error('No valid JSON found in recipe rewrite response');
      }
      
      const jsonStr = response.slice(jsonStart, jsonEnd);
      const parsed = JSON.parse(jsonStr);
      
      // Validate the structure
      if (!parsed.updatedIngredients) {
        throw new Error('Invalid recipe rewrite response format');
      }

      return {
        updatedIngredients: parsed.updatedIngredients || originalRequest.originalIngredients,
        updatedSteps: parsed.updatedSteps || originalRequest.recipeSteps,
        changes: Array.isArray(parsed.changes) ? parsed.changes : ['Ingredient substitution applied']
      };
    } catch (error) {
      console.error('Error parsing recipe rewrite response:', error);
      console.error('Raw response:', response);
      
      // Return a fallback response with manual substitution
      const fallbackIngredients = originalRequest.originalIngredients.replace(
        new RegExp(originalRequest.substitution.original, 'gi'),
        originalRequest.substitution.substitute
      );
      
      return {
        updatedIngredients: fallbackIngredients,
        updatedSteps: originalRequest.recipeSteps,
        changes: [`Replaced ${originalRequest.substitution.original} with ${originalRequest.substitution.substitute}`]
      };
    }
  }

  /**
   * Parse ingredients text into individual ingredient lines
   * @param ingredientsText Raw ingredients string
   * @returns Array of ingredient strings
   */
  parseIngredientsList(ingredientsText: string): string[] {
    return ingredientsText
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0 && !line.match(/^[-•*]\s*$/))
      .slice(0, 20); // Limit to 20 ingredients to avoid API limits
  }
}

export default new IngredientSubstitutionService();